import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Send, AlertCircle, Search, Package, X, ChevronDown, ImagePlus, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { TicketPriority, SupportTicketCategory } from '@outlaw-oasis/types';
import { formatCurrency, formatShortDate, formatDateTime } from '@outlaw-oasis/utils';
import toast from 'react-hot-toast';

interface Attachment {
  url: string;
  name: string;
}

const priorityOptions: { value: TicketPriority; label: string; description: string }[] = [
  { value: 'low', label: 'Low', description: 'General questions, not time-sensitive' },
  { value: 'medium', label: 'Medium', description: 'Standard issues needing attention' },
  { value: 'high', label: 'High', description: 'Important issues affecting your experience' },
  { value: 'urgent', label: 'Urgent', description: 'Critical issues needing immediate help' },
];

export function TicketForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    categoryId: '',
    subject: '',
    description: '',
    priority: 'medium' as TicketPriority,
    relatedOrderId: '' as string,
  });
  const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOrderDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: categoriesData } = useQuery({
    queryKey: ['support-categories'],
    queryFn: () => api.get('/support/categories'),
  });

  // Fetch user's orders
  const { data: ordersData } = useQuery({
    queryKey: ['user-orders'],
    queryFn: () => api.get('/orders'),
  });

  const categories: SupportTicketCategory[] = (categoriesData as any)?.data?.categories || [];
  const allOrders: any[] = (ordersData as any)?.data?.orders || [];

  // Filter and sort orders (most recent first, with search)
  const filteredOrders = useMemo(() => {
    let orders = [...allOrders].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    if (orderSearchTerm) {
      const search = orderSearchTerm.toLowerCase();
      orders = orders.filter(
        (order) =>
          order.orderNumber.toLowerCase().includes(search) ||
          order.status.toLowerCase().includes(search)
      );
    }

    return orders;
  }, [allOrders, orderSearchTerm]);

  // Get selected order details
  const selectedOrder = useMemo(() => {
    if (!formData.relatedOrderId) return null;
    return allOrders.find((o) => o.id === formData.relatedOrderId);
  }, [allOrders, formData.relatedOrderId]);

  const createTicketMutation = useMutation({
    mutationFn: (data: typeof formData & { attachments?: Attachment[] }) => api.post('/support/tickets', data),
    onSuccess: (response: any) => {
      toast.success('Ticket created successfully!');
      navigate(`/support/${response.data.ticketId}`);
    },
    onError: () => {
      toast.error('Failed to create ticket. Please try again.');
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Limit to 5 attachments
    if (attachments.length + files.length > 5) {
      toast.error('Maximum 5 attachments allowed');
      return;
    }

    setIsUploading(true);

    const fileArray: File[] = Array.from(files);
    for (const file of fileArray) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Only JPEG, PNG, GIF, and WebP images are allowed`);
        continue;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large. Maximum size is 10MB`);
        continue;
      }

      try {
        const uploadData = new FormData();
        uploadData.append('file', file);

        const response = await fetch(`${import.meta.env.VITE_API_URL}/uploads/ticket-attachment`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: uploadData,
        });

        const result = await response.json();

        if (result.success) {
          setAttachments((prev: Attachment[]) => [...prev, {
            url: result.data.attachmentUrl,
            name: result.data.attachmentName,
          }]);
        } else {
          toast.error(`${file.name}: ${result.message || 'Upload failed'}`);
        }
      } catch {
        toast.error(`${file.name}: Upload failed`);
      }
    }

    setIsUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev: Attachment[]) => prev.filter((_: Attachment, i: number) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.categoryId) {
      toast.error('Please select a category');
      return;
    }

    if (formData.subject.length < 5) {
      toast.error('Subject must be at least 5 characters');
      return;
    }

    if (formData.description.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }

    createTicketMutation.mutate({ ...formData, attachments });
  };

  const selectOrder = (orderId: string) => {
    setFormData({ ...formData, relatedOrderId: orderId });
    setOrderDropdownOpen(false);
    setOrderSearchTerm('');
  };

  const clearOrder = () => {
    setFormData({ ...formData, relatedOrderId: '' });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-cream mb-2">
          Category <span className="text-hot">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => setFormData({ ...formData, categoryId: category.id })}
              className={`p-4 rounded-lg border text-left transition-all ${
                formData.categoryId === category.id
                  ? 'border-gold bg-gold/10'
                  : 'border-gold/20 hover:border-gold/40 bg-leather'
              }`}
            >
              <div className="font-medium text-cream">{category.name}</div>
              {category.description && (
                <div className="text-xs text-text-muted mt-1">{category.description}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Related Order (Optional) */}
      {allOrders.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-cream mb-2">
            Related Order <span className="text-text-muted">(Optional)</span>
          </label>

          {selectedOrder ? (
            // Selected order display
            <div className="flex items-center justify-between p-4 bg-gold/10 border border-gold/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-gold" />
                <div>
                  <div className="font-mono text-gold">#{selectedOrder.orderNumber}</div>
                  <div className="text-sm text-text-secondary">
                    {formatShortDate(selectedOrder.createdAt)} - {formatCurrency(selectedOrder.total)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={clearOrder}
                className="p-1 text-text-muted hover:text-cream transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            // Order selector dropdown
            <div ref={dropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setOrderDropdownOpen(!orderDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-leather border border-gold/20 rounded-lg text-left hover:border-gold/40 transition-colors"
              >
                <span className="text-text-muted">Select an order (optional)</span>
                <ChevronDown className={`w-5 h-5 text-text-muted transition-transform ${orderDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {orderDropdownOpen && (
                <div className="absolute z-50 w-full mt-2 bg-surface border border-gold/20 rounded-lg shadow-xl overflow-hidden">
                  {/* Search */}
                  <div className="p-3 border-b border-gold/10">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                      <input
                        type="text"
                        value={orderSearchTerm}
                        onChange={(e) => setOrderSearchTerm(e.target.value)}
                        placeholder="Search orders..."
                        className="w-full pl-10 pr-4 py-2 bg-leather border border-gold/20 rounded-lg text-cream placeholder-text-muted focus:border-gold focus:outline-none text-sm"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Orders list */}
                  <div className="max-h-64 overflow-y-auto">
                    {filteredOrders.length === 0 ? (
                      <div className="p-4 text-center text-text-muted text-sm">
                        No orders found
                      </div>
                    ) : (
                      filteredOrders.map((order) => (
                        <button
                          key={order.id}
                          type="button"
                          onClick={() => selectOrder(order.id)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-leather/50 transition-colors text-left border-b border-gold/5 last:border-0"
                        >
                          <Package className="w-5 h-5 text-gold/60 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gold text-sm">#{order.orderNumber}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                order.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                order.status === 'pending_payment' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {order.status === 'pending_payment' ? 'Pending' : order.status}
                              </span>
                            </div>
                            <div className="text-xs text-text-muted mt-0.5">
                              {formatDateTime(order.createdAt)} - {order.itemCount} item{order.itemCount !== 1 ? 's' : ''} - {formatCurrency(order.total)}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <p className="text-xs text-text-muted mt-1">
            Link an order to help our support team assist you faster
          </p>
        </div>
      )}

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-cream mb-2">
          Subject <span className="text-hot">*</span>
        </label>
        <input
          type="text"
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          placeholder="Brief summary of your issue"
          maxLength={200}
          className="w-full px-4 py-3 bg-leather border border-gold/20 rounded-lg text-cream placeholder-text-muted focus:border-gold focus:outline-none"
        />
        <div className="text-xs text-text-muted mt-1 text-right">
          {formData.subject.length}/200
        </div>
      </div>

      {/* Priority */}
      <div>
        <label className="block text-sm font-medium text-cream mb-2">
          Priority <span className="text-hot">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {priorityOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFormData({ ...formData, priority: option.value })}
              className={`p-3 rounded-lg border text-center transition-all ${
                formData.priority === option.value
                  ? 'border-gold bg-gold/10'
                  : 'border-gold/20 hover:border-gold/40 bg-leather'
              }`}
            >
              <div className={`font-medium ${
                option.value === 'urgent' ? 'text-red-400' :
                option.value === 'high' ? 'text-orange-400' :
                option.value === 'medium' ? 'text-blue-400' :
                'text-gray-400'
              }`}>
                {option.label}
              </div>
            </button>
          ))}
        </div>
        <p className="text-xs text-text-muted mt-2">
          {priorityOptions.find(p => p.value === formData.priority)?.description}
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-cream mb-2">
          Description <span className="text-hot">*</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Please provide as much detail as possible about your issue..."
          rows={6}
          maxLength={5000}
          className="w-full px-4 py-3 bg-leather border border-gold/20 rounded-lg text-cream placeholder-text-muted focus:border-gold focus:outline-none resize-none"
        />
        <div className="text-xs text-text-muted mt-1 text-right">
          {formData.description.length}/5000
        </div>
      </div>

      {/* Attachments */}
      <div>
        <label className="block text-sm font-medium text-cream mb-2">
          Attachments <span className="text-text-muted">(Optional - Max 5 images)</span>
        </label>

        {/* Uploaded attachments */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-3">
            {attachments.map((attachment, index) => (
              <div key={index} className="relative group">
                <img
                  src={attachment.url}
                  alt={attachment.name}
                  className="w-20 h-20 object-cover rounded-lg border border-gold/20"
                />
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-hot text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate rounded-b-lg">
                  {attachment.name}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        {attachments.length < 5 && (
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              onChange={handleFileUpload}
              disabled={isUploading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            />
            <div className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gold/30 rounded-lg text-text-secondary hover:border-gold/50 hover:text-cream transition-colors ${isUploading ? 'opacity-50' : ''}`}>
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <ImagePlus className="w-5 h-5" />
                  <span>Click or drag images here</span>
                </>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-text-muted mt-2">
          Supported formats: JPEG, PNG, GIF, WebP. Max 10MB per image.
        </p>
      </div>

      {/* Info box */}
      <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-300">
          <p className="font-medium mb-1">What happens next?</p>
          <p className="text-blue-300/80">
            You'll receive an email confirmation when your ticket is created. Our support team
            typically responds within 24-48 hours. You'll be notified via email whenever there's
            an update on your ticket.
          </p>
        </div>
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={createTicketMutation.isPending}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gold text-leather font-medium rounded-lg hover:bg-gold-bright transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {createTicketMutation.isPending ? (
          <>
            <div className="w-5 h-5 border-2 border-leather/30 border-t-leather rounded-full animate-spin" />
            Creating Ticket...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Submit Ticket
          </>
        )}
      </button>
    </form>
  );
}
